import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import html2canvas from 'html2canvas'
import {
  RiBugLine,
  RiLightbulbLine,
  RiQuestionLine,
  RiScreenshotLine,
  RiScissorsCutLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiCheckLine,
} from '@remixicon/react'
import Drawer from '../ui/Drawer'
import { Button } from '../ui/Button'
import { Input, Textarea } from '../ui/Input'
import { api } from '../../utils/api'
import { cx } from '../../lib/utils'

type ReportType = 'bug' | 'feature' | 'question'

interface BugReportDrawerProps {
  className?: string
}

export default function BugReportDrawer({ className }: BugReportDrawerProps) {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [reportType, setReportType] = useState<ReportType>('bug')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Selection state
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null)
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  const reportTypes: { type: ReportType; icon: typeof RiBugLine; label: string }[] = [
    { type: 'bug', icon: RiBugLine, label: t('feedback.typeBug') },
    { type: 'feature', icon: RiLightbulbLine, label: t('feedback.typeFeature') },
    { type: 'question', icon: RiQuestionLine, label: t('feedback.typeQuestion') },
  ]

  const resetForm = useCallback(() => {
    setTitle('')
    setDescription('')
    setScreenshot(null)
    setReportType('bug')
    setSubmitSuccess(false)
    setSubmitError(null)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    // Delay reset to allow drawer animation to complete
    setTimeout(resetForm, 300)
  }, [resetForm])

  const captureFullScreen = useCallback(async () => {
    setIsCapturing(true)
    setIsOpen(false) // Hide drawer during capture

    // Wait for drawer to close
    await new Promise(resolve => setTimeout(resolve, 350))

    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        backgroundColor: null,
      })
      setScreenshot(canvas.toDataURL('image/png'))
    } catch (error) {
      console.error('Screenshot capture failed:', error)
    } finally {
      setIsCapturing(false)
      setIsOpen(true)
    }
  }, [])

  const startAreaSelection = useCallback(() => {
    setIsOpen(false) // Hide drawer during selection
    setIsSelecting(true)
    setSelectionStart(null)
    setSelectionEnd(null)
  }, [])

  const handleSelectionMouseDown = useCallback((e: React.MouseEvent) => {
    setSelectionStart({ x: e.clientX, y: e.clientY })
    setSelectionEnd({ x: e.clientX, y: e.clientY })
  }, [])

  const handleSelectionMouseMove = useCallback((e: React.MouseEvent) => {
    if (selectionStart) {
      setSelectionEnd({ x: e.clientX, y: e.clientY })
    }
  }, [selectionStart])

  const handleSelectionMouseUp = useCallback(async () => {
    if (!selectionStart || !selectionEnd) {
      setIsSelecting(false)
      setIsOpen(true)
      return
    }

    const x = Math.min(selectionStart.x, selectionEnd.x)
    const y = Math.min(selectionStart.y, selectionEnd.y)
    const width = Math.abs(selectionEnd.x - selectionStart.x)
    const height = Math.abs(selectionEnd.y - selectionStart.y)

    if (width < 10 || height < 10) {
      // Selection too small, cancel
      setIsSelecting(false)
      setIsOpen(true)
      return
    }

    setIsSelecting(false)

    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        backgroundColor: null,
        x,
        y,
        width,
        height,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      })
      setScreenshot(canvas.toDataURL('image/png'))
    } catch (error) {
      console.error('Area capture failed:', error)
    }

    setSelectionStart(null)
    setSelectionEnd(null)
    setIsOpen(true)
  }, [selectionStart, selectionEnd])

  const cancelSelection = useCallback(() => {
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
    setIsOpen(true)
  }, [])

  const removeScreenshot = useCallback(() => {
    setScreenshot(null)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Collect metadata
      const metadata = {
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
      }

      await api.post('/api/feedback', {
        type: reportType,
        title: title.trim(),
        description: description.trim(),
        screenshot,
        metadata,
      })

      setSubmitSuccess(true)
      // Auto-close after showing success message
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('feedback.submitError')
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [reportType, title, description, screenshot, t, handleClose])

  // Selection rectangle dimensions
  const getSelectionRect = () => {
    if (!selectionStart || !selectionEnd) return null
    return {
      left: Math.min(selectionStart.x, selectionEnd.x),
      top: Math.min(selectionStart.y, selectionEnd.y),
      width: Math.abs(selectionEnd.x - selectionStart.x),
      height: Math.abs(selectionEnd.y - selectionStart.y),
    }
  }

  const selectionRect = getSelectionRect()

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cx(
          'fixed bottom-6 right-6 z-40 flex h-10 w-10 items-center justify-center rounded',
          'border border-gray-300 bg-white text-primary-600 shadow-sm transition-colors hover:bg-gray-100',
          'focus:outline-none focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-100',
          'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-50 dark:hover:bg-gray-800',
          isCapturing && 'opacity-0 pointer-events-none',
          className
        )}
        aria-label={t('feedback.reportIssue')}
      >
        <RiBugLine className="size-5" />
      </button>

      {/* Area Selection Overlay */}
      {isSelecting && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-[100] cursor-crosshair"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
          onMouseDown={handleSelectionMouseDown}
          onMouseMove={handleSelectionMouseMove}
          onMouseUp={handleSelectionMouseUp}
        >
          {/* Selection Rectangle */}
          {selectionRect && (
            <div
              className="absolute border-2 border-primary-500 bg-primary-500/10"
              style={{
                left: selectionRect.left,
                top: selectionRect.top,
                width: selectionRect.width,
                height: selectionRect.height,
              }}
            />
          )}

          {/* Instructions */}
          <div className="absolute left-1/2 top-4 -translate-x-1/2 transform rounded-lg bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">
            {t('feedback.selectAreaInstructions')}
          </div>

          {/* Cancel Button */}
          <button
            onClick={cancelSelection}
            className="absolute right-4 top-4 rounded-full bg-gray-900 p-2 text-white hover:bg-gray-800"
          >
            <RiCloseLine className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Drawer */}
      <Drawer
        open={isOpen}
        onClose={handleClose}
        title={t('feedback.title')}
        subtitle={t('feedback.subtitle')}
        size="md"
        footer={
          !submitSuccess && (
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isSubmitting}
                loadingText={t('feedback.submitting')}
                disabled={!title.trim()}
              >
                {t('feedback.submit')}
              </Button>
            </div>
          )
        }
      >
        {submitSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <RiCheckLine className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('feedback.successTitle')}
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('feedback.successMessage')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Report Type Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('feedback.reportType')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {reportTypes.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setReportType(type)}
                    className={cx(
                      'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-all',
                      reportType === type
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-950/50 dark:text-primary-300'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <Input
              label={t('feedback.titleLabel')}
              placeholder={t('feedback.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            {/* Description */}
            <Textarea
              label={t('feedback.descriptionLabel')}
              placeholder={t('feedback.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />

            {/* Screenshot Section */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('feedback.screenshot')}
              </label>

              {screenshot ? (
                <div className="relative">
                  <img
                    src={screenshot}
                    alt="Screenshot"
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                  <button
                    onClick={removeScreenshot}
                    className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1.5 text-white shadow-md hover:bg-red-600"
                    aria-label={t('feedback.removeScreenshot')}
                  >
                    <RiDeleteBinLine className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={captureFullScreen}
                    icon={<RiScreenshotLine className="h-4 w-4" />}
                    disabled={isCapturing}
                  >
                    {t('feedback.captureScreen')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={startAreaSelection}
                    icon={<RiScissorsCutLine className="h-4 w-4" />}
                  >
                    {t('feedback.selectArea')}
                  </Button>
                </div>
              )}
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {submitError}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  )
}
